/**
 * /admin/students — the roster.
 *
 * There is no self-signup: an admin creates every account, which was invisible
 * while the app served one seeded student and is the whole job now that general
 * exams run for real cohorts. So this screen is where the people sitting an
 * exam come from, and the credentials it produces are read out to a student
 * rather than chosen by them — hence the generator and the copy affordance
 * rather than a bare password box.
 *
 * Nothing here deletes. An account is referenced by every attempt, recording
 * and grade it produced, so the destructive-looking action is deactivation and
 * it is reversible.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Copy, RefreshCw, UserPlus } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  Dialog,
  Input,
  Select,
  Table,
  TableEmpty,
  TBody,
  TD,
  TH,
  THead,
  TR,
  useToast,
} from "@/components/ui";
import { CreateUserInput, MIN_PASSWORD_LENGTH } from "@exam/shared";
import type { AdminUser } from "@exam/shared";
import { isApiError } from "@/lib/api";
import { ErrorNote, FixtureBanner, Loading, PageTitle } from "./components/Shell";
import { createStudent, fetchUsers, updateStudent } from "./data/client";
import { useAsync } from "./lib/useAsync";

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

type UserRole = AdminUser["role"];
type ActiveFilter = "active" | "inactive" | "all";

/** `undefined` is "every account" on the wire, so the three states map by hand. */
const ACTIVE_PARAM: Record<ActiveFilter, boolean | undefined> = {
  active: true,
  inactive: false,
  all: undefined,
};

export function StudentsPage() {
  const { toast } = useToast();

  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>("active");
  const [offset, setOffset] = useState(0);

  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [deactivating, setDeactivating] = useState<AdminUser | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Typing a name should not fire a request per keystroke; a short pause is
  // enough to tell "still typing" from "done".
  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(search.trim());
      setOffset(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const state = useAsync(
    () =>
      fetchUsers({
        q: query === "" ? undefined : query,
        active: ACTIVE_PARAM[activeFilter],
        limit: PAGE_SIZE,
        offset,
      }),
    [query, activeFilter, offset],
  );

  const users = state.data?.users ?? [];
  const total = state.data?.total ?? 0;

  const setActive = async (user: AdminUser, active: boolean) => {
    setBusyId(user.id);
    try {
      await updateStudent(user.id, { active });
      setDeactivating(null);
      state.reload();
      toast({
        tone: "success",
        title: active ? `${user.name} can sign in again.` : `${user.name} deactivated.`,
      });
    } catch (err) {
      toast({
        tone: "error",
        title: active ? "Could not reactivate the account." : "Could not deactivate the account.",
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4">
      <PageTitle
        title="Students"
        meta="Accounts are created here — there is no self-signup. Accounts are never deleted: deactivating one stops the sign-in and keeps every attempt, recording and grade intact."
        actions={
          <Button variant="primary" icon={<UserPlus />} onClick={() => setAdding(true)}>
            Add student
          </Button>
        }
      />

      <FixtureBanner />

      <Card>
        <div className="flex flex-wrap items-end gap-3 border-b border-border px-4 py-3">
          <div className="min-w-56 flex-1">
            <Input
              label="Search"
              type="search"
              placeholder="Name or email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="w-40">
            <Select
              label="Status"
              value={activeFilter}
              onChange={(e) => {
                setActiveFilter(e.target.value as ActiveFilter);
                setOffset(0);
              }}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="all">All</option>
            </Select>
          </div>
          <p className="tabular ml-auto pb-2 text-2xs text-ink-faint">
            {total} account{total === 1 ? "" : "s"}
          </p>
        </div>

        {state.loading ? (
          <Loading />
        ) : state.error ? (
          <div className="p-4">
            <ErrorNote error={state.error} />
          </div>
        ) : (
          <>
            <Table>
              <THead>
                <tr>
                  <TH>Name</TH>
                  <TH>Email</TH>
                  <TH>Role</TH>
                  <TH>Status</TH>
                  <TH align="right">Attempts</TH>
                  <TH />
                </tr>
              </THead>
              <TBody>
                {users.length === 0 ? (
                  <TableEmpty colSpan={6}>
                    {query !== "" || activeFilter !== "active"
                      ? "No accounts match this search."
                      : "No students yet — add the first one."}
                  </TableEmpty>
                ) : (
                  users.map((user) => (
                    <TR key={user.id}>
                      <TD>
                        <Link
                          to={`/admin/students/${user.id}`}
                          className="font-medium text-ink hover:underline"
                        >
                          {user.name}
                        </Link>
                      </TD>
                      <TD className="text-ink-muted">{user.email}</TD>
                      <TD className="text-ink-muted">{user.role}</TD>
                      <TD>
                        <Badge tone={user.active ? "answered" : "neutral"}>
                          {user.active ? "active" : "inactive"}
                        </Badge>
                      </TD>
                      <TD align="right" className="tabular">
                        {user.attempt_count}
                      </TD>
                      <TD align="right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={busyId === user.id}
                            onClick={() => setEditing(user)}
                          >
                            Edit
                          </Button>
                          {user.active ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={busyId === user.id}
                              onClick={() => setDeactivating(user)}
                            >
                              Deactivate
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={busyId === user.id}
                              onClick={() => void setActive(user, true)}
                            >
                              Reactivate
                            </Button>
                          )}
                        </div>
                      </TD>
                    </TR>
                  ))
                )}
              </TBody>
            </Table>

            {total > PAGE_SIZE ? (
              <div className="flex items-center justify-between border-t border-border px-4 py-3">
                <p className="tabular text-2xs text-ink-faint">
                  {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    disabled={offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    disabled={offset + PAGE_SIZE >= total}
                    onClick={() => setOffset(offset + PAGE_SIZE)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            ) : null}
          </>
        )}
      </Card>

      <AddStudentDialog
        open={adding}
        onClose={() => setAdding(false)}
        onCreated={(user) => {
          setAdding(false);
          state.reload();
          toast({
            tone: "success",
            title: `${user.name} added.`,
            description: "Hand them the password — it cannot be read back later.",
          });
        }}
      />

      <EditStudentDialog
        student={editing}
        onClose={() => setEditing(null)}
        onSaved={(user, passwordReset) => {
          setEditing(null);
          state.reload();
          toast({
            tone: "success",
            title: `${user.name} updated.`,
            description: passwordReset ? "Their password was reset." : undefined,
          });
        }}
      />

      <Dialog
        open={deactivating !== null}
        onClose={() => setDeactivating(null)}
        title={deactivating ? `Deactivate ${deactivating.name}?` : "Deactivate"}
        footer={
          <>
            <Button onClick={() => setDeactivating(null)}>Cancel</Button>
            <Button
              variant="danger"
              disabled={busyId !== null}
              onClick={() => {
                if (deactivating) void setActive(deactivating, false);
              }}
            >
              Deactivate
            </Button>
          </>
        }
      >
        <div className="space-y-2 text-xs text-ink-muted">
          <p>
            They can no longer sign in, and they stop appearing in the pickers for new
            sittings.
          </p>
          <p>
            Every attempt, recording and grade they already have is kept, and stays visible
            on their progress screen. Reactivating restores the sign-in.
          </p>
        </div>
      </Dialog>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Add                                                                 */
/* ------------------------------------------------------------------ */

function AddStudentDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (user: AdminUser) => void;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<UserRole>("student");
  const [errors, setErrors] = useState<FieldErrors>({});
  const [busy, setBusy] = useState(false);

  // A dialog that reopens holding the last student's details is how the wrong
  // person gets created, so the form is blank every time it is opened.
  useEffect(() => {
    if (!open) return;
    setName("");
    setEmail("");
    setPassword("");
    setRole("student");
    setErrors({});
  }, [open]);

  const submit = async () => {
    const found = validate({ name, email, password, requirePassword: true });
    setErrors(found);
    if (hasErrors(found)) return;

    setBusy(true);
    try {
      onCreated(
        await createStudent({
          name: name.trim(),
          email: email.trim(),
          password,
          role,
        }),
      );
    } catch (err) {
      // A duplicate email is a fact about one field, and the server phrases it
      // better than this screen could, so it is shown against that field
      // rather than thrown at a toast the admin has to translate.
      if (isApiError(err, "conflict")) {
        setErrors({ email: err.message });
      } else {
        setErrors({ form: err instanceof Error ? err.message : "Could not add the student." });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add student"
      description="They cannot sign themselves up — this creates the account and its password."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={busy} onClick={() => void submit()}>
            Add student
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input
          label="Name"
          value={name}
          error={errors.name}
          autoComplete="off"
          onChange={(e) => setName(e.target.value)}
        />
        <Input
          label="Email"
          type="email"
          value={email}
          error={errors.email}
          autoComplete="off"
          onChange={(e) => setEmail(e.target.value)}
        />
        <PasswordField
          label="Password"
          hint={`At least ${MIN_PASSWORD_LENGTH} characters. Read it out or copy it now — it cannot be shown again.`}
          value={password}
          error={errors.password}
          onChange={setPassword}
        />
        <Select
          label="Role"
          value={role}
          onChange={(e) => setRole(e.target.value as UserRole)}
        >
          <option value="student">Student</option>
          <option value="admin">Admin</option>
        </Select>
        {/* An admin can extend clocks, end sittings and permanently delete a
            student's recordings. On a dialog titled "Add student" that is one
            mis-click away, so it has to be said rather than assumed. */}
        {role === "admin" ? (
          <p className="rounded-md border border-review/30 bg-review-soft px-3 py-2 text-2xs text-review">
            This creates a full admin: they can see every student's recordings
            and answers, change grades, and permanently delete an attempt.
          </p>
        ) : null}
        {errors.form ? <ErrorNote error={new Error(errors.form)} /> : null}
      </div>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* Edit                                                                */
/* ------------------------------------------------------------------ */

function EditStudentDialog({
  student,
  onClose,
  onSaved,
}: {
  student: AdminUser | null;
  onClose: () => void;
  onSaved: (user: AdminUser, passwordReset: boolean) => void;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<FieldErrors>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!student) return;
    setName(student.name);
    setEmail(student.email);
    // Blank on purpose, and re-blanked per student: an admin editing a name
    // must not reset a password as a side effect of the form being prefilled.
    setPassword("");
    setErrors({});
  }, [student]);

  const submit = async () => {
    if (!student) return;
    const found = validate({ name, email, password, requirePassword: false });
    setErrors(found);
    if (hasErrors(found)) return;

    const patch: { name?: string; email?: string; password?: string } = {};
    if (name.trim() !== student.name) patch.name = name.trim();
    if (email.trim() !== student.email) patch.email = email.trim();
    if (password !== "") patch.password = password;

    // The contract rejects an empty patch, and "Save" with nothing changed is a
    // reasonable thing for someone to click.
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }

    setBusy(true);
    try {
      onSaved(await updateStudent(student.id, patch), patch.password !== undefined);
    } catch (err) {
      if (isApiError(err, "conflict")) {
        setErrors({ email: err.message });
      } else {
        setErrors({ form: err instanceof Error ? err.message : "Could not save the changes." });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={student !== null}
      onClose={onClose}
      title={student ? `Edit ${student.name}` : "Edit student"}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={busy} onClick={() => void submit()}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input
          label="Name"
          value={name}
          error={errors.name}
          onChange={(e) => setName(e.target.value)}
        />
        <Input
          label="Email"
          type="email"
          value={email}
          error={errors.email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <PasswordField
          label="Reset password"
          hint="Leave blank to keep the current password."
          value={password}
          error={errors.password}
          onChange={setPassword}
        />
        {errors.form ? <ErrorNote error={new Error(errors.form)} /> : null}
      </div>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* Password field                                                      */
/* ------------------------------------------------------------------ */

/**
 * Shown in the clear, with a generator and a copy button.
 *
 * Masking would be theatre: whoever is at this screen is about to read the
 * password out to the student anyway, and a password they cannot see is one
 * they will mistype into the handover.
 */
function PasswordField({
  label,
  hint,
  value,
  error,
  onChange,
}: {
  label: string;
  hint: string;
  value: string;
  error?: string;
  onChange: (next: string) => void;
}) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // Clipboard access can be refused (insecure origin, denied permission);
      // the password is on screen, so this is a nuisance rather than a failure.
      toast({ tone: "warning", title: "Could not copy — select the field instead." });
    }
  };

  return (
    <div className="flex items-end gap-2">
      <div className="min-w-0 flex-1">
        <Input
          label={label}
          hint={hint}
          type="text"
          value={value}
          error={error}
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
      <div className="flex shrink-0 items-center gap-1 pb-5">
        <Button
          size="sm"
          icon={<RefreshCw />}
          onClick={() => onChange(generatePassword())}
        >
          Generate
        </Button>
        <Button
          size="sm"
          icon={copied ? <Check /> : <Copy />}
          disabled={value === ""}
          onClick={() => void copy()}
        >
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>
    </div>
  );
}

/**
 * Readable by construction: alternating consonants and vowels make syllables a
 * person can say down a corridor, which matters more here than entropy density
 * because the string is transcribed by hand at least once.
 */
function generatePassword(): string {
  const CONSONANTS = "bcdfghjkmnprstvwxz";
  const VOWELS = "aeiou";

  const syllable = () => `${pick(CONSONANTS)}${pick(VOWELS)}`;
  const word = () => `${syllable()}${syllable()}${syllable()}`;
  return `${word()}-${word()}${pick("0123456789")}${pick("0123456789")}`;
}

function pick(alphabet: string): string {
  const buf = new Uint32Array(1);
  crypto.getRandomValues(buf);
  return alphabet[buf[0]! % alphabet.length]!;
}

/* ------------------------------------------------------------------ */
/* Validation                                                          */
/* ------------------------------------------------------------------ */

interface FieldErrors {
  name?: string;
  email?: string;
  password?: string;
  form?: string;
}

function hasErrors(errors: FieldErrors): boolean {
  return Object.values(errors).some((message) => message !== undefined);
}

/**
 * Checked against the contract's own schemas rather than a second copy of the
 * rules here, so a change to `CreateUserInput` cannot leave this screen quietly
 * accepting what the API will reject.
 */
function validate({
  name,
  email,
  password,
  requirePassword,
}: {
  name: string;
  email: string;
  password: string;
  requirePassword: boolean;
}): FieldErrors {
  const errors: FieldErrors = {};

  if (!CreateUserInput.shape.name.safeParse(name.trim()).success) {
    errors.name = "Enter a name.";
  }
  if (!CreateUserInput.shape.email.safeParse(email.trim()).success) {
    errors.email = "Enter a valid email address.";
  }
  if (requirePassword || password !== "") {
    if (!CreateUserInput.shape.password.safeParse(password).success) {
      errors.password = `Use at least ${MIN_PASSWORD_LENGTH} characters.`;
    }
  }

  return errors;
}
